import { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import SchedulePage from './SchedulePage';
import './firebase';

function App() {
  const heroContentRef = useRef(null);
  const appRef = useRef(null);
  const prevPathRef = useRef(null);
  const [capabilityIndex, setCapabilityIndex] = useState(0);
  const [capabilityDir, setCapabilityDir] = useState('next');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHeroBgActive, setIsHeroBgActive] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const marketingCapabilities = useMemo(() => ([
    {
      imageTitle: 'BRAND STRATEGY & POSITIONING',
      subtitle: 'Built to stand out. Designed to last.',
      body:
        "We don't just define your brand — we engineer how it shows up, competes, and wins. From positioning and messaging to visual identity, we align your brand around a clear point of view that's confident, recognizable, and impossible to confuse with competitors.",
      titleAnim: 'sweep-left',
      image: '/vibrantfinal.png',
    },
    {
      imageTitle: 'MARKET INTELLIGENCE & OPPORTUNITY MAPPING',
      subtitle: 'Turning attention into action.',
      body:
        'We analyze your market the way operators do. By studying your audience, competitors, and category dynamics, we uncover where demand lives, where focus matters, and how to move decisively in the right direction.',
      titleAnim: 'glide-right',
      image: '/neoncarfinal.png',
    },
    {
      imageTitle: 'PERFORMANCE-DRIVEN DIGITAL MARKETING',
      subtitle: 'Marketing that performs, scales, and adapts.',
      body:
        'We build digital systems designed to attract, convert, and retain. From SEO and paid acquisition to email and social, every channel is intentional, measurable, and tied directly to growth.',
      titleAnim: 'float-left',
      image: '/boatfinal.png',
    },
  ]), []);

  const activeCapability = marketingCapabilities[capabilityIndex] || marketingCapabilities[0];

  const clamp01 = (n) => Math.min(1, Math.max(0, n));

  const goCapability = (dir) => {
    setCapabilityDir(dir);
    setCapabilityIndex((prev) => {
      const next = dir === 'prev' ? prev - 1 : prev + 1;
      const len = marketingCapabilities.length;
      return (next + len) % len;
    });
  };

  const handleCapabilitiesKeyDown = (e) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goCapability('prev');
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goCapability('next');
    }
  };

  const closeMenu = () => setIsMenuOpen(false);

  const navigateTo = (path) => {
    closeMenu();
    navigate(path);
  };

  const scrollToElTop = (el, extraOffset = 0) => {
    const rootEl = appRef.current;
    if (!rootEl || !el) return;
    const topbar = document.querySelector('.topbar');
    const navHeight = topbar ? topbar.getBoundingClientRect().height : 0;
    const rootRect = rootEl.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const nextTop = rootEl.scrollTop + (elRect.top - rootRect.top) - navHeight - extraOffset;
    rootEl.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
  };

  const scrollToSectionTop = (id) => {
    const rootEl = appRef.current;
    if (!rootEl) return;

    if (id === 'home') {
      rootEl.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const direct = document.getElementById(id);
    // If the ID points to a non-section element (e.g. a dedicated anchor), scroll to it.
    // If it points to a <section>, prefer a nested scroll anchor for better landing alignment.
    if (direct && String(direct.tagName || '').toLowerCase() !== 'section') {
      scrollToElTop(direct);
      return;
    }

    const section =
      (direct && String(direct.tagName || '').toLowerCase() === 'section')
        ? direct
        : document.querySelector(`section#${id}`);
    if (!section) return;

    // For marketing section, explicitly target the "What we do" heading with extra offset
    if (id === 'marketing') {
      const whatWeDoHeading = section.querySelector('h2.section-title[data-scroll-anchor="true"]');
      if (whatWeDoHeading) {
        scrollToElTop(whatWeDoHeading, 40);
        return;
      }
    }

    // For design and about sections, apply extra offset
    if (id === 'design' || id === 'about') {
      const anchor = section.querySelector('[data-scroll-anchor="true"]');
      scrollToElTop(anchor || section, 10);
      return;
    }

    // Prefer an explicit anchor inside the section (e.g., the section title),
    // so landing positions feel consistent even when sections have large padding.
    const anchor = section.querySelector('[data-scroll-anchor="true"]');
    scrollToElTop(anchor || section);
  };

  const navigateToSection = (id) => {
    closeMenu();
    if (location.pathname !== '/') {
      navigate('/', { replace: false });
      // Allow route to render, then scroll (2x rAF is more reliable than a fixed timeout).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToSectionTop(id);
        });
      });
      return;
    }

    scrollToSectionTop(id);
  };

  useEffect(() => {
    if (!isMenuOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMenuOpen]);

  useEffect(() => {
    // When returning to the home route, treat it like a "home redirect":
    // reset scroll so users see the page from the top.
    const prev = prevPathRef.current;
    prevPathRef.current = location.pathname;
    if (location.pathname === '/' && prev && prev !== '/') {
      const rootEl = appRef.current;
      if (rootEl) {
        rootEl.scrollTo({ top: 0, behavior: 'auto' });
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    // Smooth scroll for anchor links
    const handleAnchorClick = (e) => {
      // Get the anchor element (works even if clicking on child elements)
      const anchor = e.target.closest('a[href^="#"]');
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (href && href.startsWith('#')) {
          e.preventDefault();
          e.stopPropagation();
          const targetId = href.substring(1);

          scrollToSectionTop(targetId);
          closeMenu();
        }
      }
    };

    // Add event listeners for all anchor links (including dynamically added ones)
    const addAnchorListeners = () => {
      const anchorLinks = document.querySelectorAll('a[href^="#"]');
      anchorLinks.forEach(link => {
        link.removeEventListener('click', handleAnchorClick);
        link.addEventListener('click', handleAnchorClick);
      });
    };

    // Add listeners initially and after a short delay to catch all elements
    addAnchorListeners();
    const t = setTimeout(addAnchorListeners, 100);

    return () => {
      clearTimeout(t);
      const anchorLinks = document.querySelectorAll('a[href^="#"]');
      anchorLinks.forEach(link => {
        link.removeEventListener('click', handleAnchorClick);
      });
    };
  }, []);

  useEffect(() => {
    if (location.pathname !== '/') return;
    // Hero entrance animation
    requestAnimationFrame(() => {
      if (heroContentRef.current) {
        heroContentRef.current.classList.add('animate-in', 'reveal-in');
      }
    });

    const rootEl = appRef.current;
    if (!rootEl) return;

    const prefersReducedMotion = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

    // "One-shot" reveals (used everywhere except scrubbed sections like Marketing)
    const targets = Array.from(rootEl.querySelectorAll('[data-reveal]:not([data-reveal-scrub])'));
    if (targets.length === 0) return;

    if (prefersReducedMotion) {
      targets.forEach(el => el.classList.add('animate-in', 'reveal-in'));
      return;
    }

    // Jest/jsdom (and some older browsers) may not provide IntersectionObserver.
    if (typeof IntersectionObserver === 'undefined') {
      targets.forEach(el => el.classList.add('animate-in', 'reveal-in'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in', 'reveal-in');
          observer.unobserve(entry.target);
        }
      });
    }, {
      root: rootEl,
      threshold: 0.15,
      rootMargin: '0px 0px -10% 0px'
    });

    targets.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/') return;
    const rootEl = appRef.current;
    if (!rootEl) return;

    const marketingSection = rootEl.querySelector('#marketing');
    if (!marketingSection) return;

    const prefersReducedMotion = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

    const pieces = Array.from(marketingSection.querySelectorAll('[data-reveal-scrub]'));
    if (pieces.length === 0) return;

    // Reset progress when (re)entering home so pieces don't get stuck invisible.
    pieces.forEach((el) => el.style.setProperty('--reveal-p', '0'));

    const titleEl = marketingSection.querySelector('.section-title') || pieces[0];
    if (!titleEl) return;

    if (prefersReducedMotion) {
      pieces.forEach((el) => el.style.setProperty('--reveal-p', '1'));
      return;
    }

    let raf = 0;
    const update = () => {
      raf = 0;

      const rootRect = rootEl.getBoundingClientRect();
      const viewportCenterY = rootEl.clientHeight / 2;

      const titleRect = titleEl.getBoundingClientRect();
      const titleCenterY = (titleRect.top - rootRect.top) + titleRect.height / 2;

      // Progress goes 0 -> 1 as the title moves from near-bottom to center.
      // This ensures the animation is visible (not finished before the section enters view),
      // and feels "slow" + scroll-synced.
      const startLine = rootEl.clientHeight * 0.96; // ~bottom of viewport
      const endLine = rootEl.clientHeight * 0.50;   // center of viewport
      const rawProgress = clamp01((startLine - titleCenterY) / (startLine - endLine));
      // Smoothstep easing to avoid feeling jumpy at start/end.
      const progress = rawProgress * rawProgress * (3 - 2 * rawProgress);

      // Each piece gets a long reveal window (overlapping) so the motion feels gradual.
      const n = pieces.length;
      const span = 0.52; // longer per-piece reveal window => slower feel
      const step = n > 1 ? (1 - span) / (n - 1) : 1;

      pieces.forEach((el, idx) => {
        const start = idx * step;
        const p = clamp01((progress - start) / span);
        el.style.setProperty('--reveal-p', String(p));
      });
    };

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    };

    update();
    rootEl.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      rootEl.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [location.pathname]);

  useEffect(() => {
    const rootEl = appRef.current;
    if (!rootEl) return;

    const heroEl = rootEl.querySelector('#home');
    if (!heroEl) {
      setIsHeroBgActive(false);
      return;
    }

    let raf = 0;
    const update = () => {
      raf = 0;
      const topbar = document.querySelector('.topbar');
      const navHeight = topbar ? topbar.getBoundingClientRect().height : 0;
      const heroHeight = heroEl.getBoundingClientRect().height || 0;
      // Keep hero background active while we're within the hero section.
      const active = rootEl.scrollTop < Math.max(0, heroHeight - navHeight * 0.5);
      setIsHeroBgActive(active);
    };

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    };

    update();
    rootEl.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      rootEl.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [location.pathname]);

  return (
    <div
      className="App"
      ref={appRef}
      data-menu-open={isMenuOpen ? 'true' : 'false'}
      data-hero-bg={isHeroBgActive ? 'true' : 'false'}
    >
      {/* Minimal header (no navbar); MENU opens full-screen overlay */}
      <header className="topbar" aria-label="Site header">
        <button type="button" className="logo logo--button" onClick={() => navigateToSection('home')} aria-label="Go to top">
          <img src="/LOGO GOLD AND WHITE TRANSPARENT.png" alt="Auradrobe Marketing Logo" className="logo-image" />
          <span className="logo-text">
            <span className="logo-title">
              <span className="brand-name">Auradrobe</span>
            </span>
            <span className="logo-tagline">Digital Innovation Partners</span>
          </span>
        </button>

        <button
          type="button"
          className="menu-button"
          onClick={() => setIsMenuOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={isMenuOpen ? 'true' : 'false'}
        >
          MENU
        </button>
      </header>

      {isMenuOpen && (
        <div className="menu-overlay" role="dialog" aria-modal="true" aria-label="Menu">
          <button type="button" className="menu-close" onClick={closeMenu} aria-label="Close menu">
            ×
          </button>

          <nav className="menu-panel" aria-label="Primary">
            <button type="button" className="menu-item is-active" onClick={() => navigateToSection('home')}>HOME</button>
            <button type="button" className="menu-item" onClick={() => navigateToSection('marketing')}>MARKETING</button>
            <button type="button" className="menu-item" onClick={() => navigateToSection('design')}>DESIGN</button>
            <button type="button" className="menu-item" onClick={() => navigateToSection('about')}>ABOUT</button>
            <button type="button" className="menu-item" onClick={() => navigateTo('/schedule')}>SCHEDULE</button>
          </nav>
        </div>
      )}
      <Routes>
        <Route
          path="/"
          element={(
            <>
      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="animated-bg">
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
        </div>
        <div className="hero-grid">
          <div className="hero-content" ref={heroContentRef}>
            <h1 className="hero-title">
              <span className="title-line title-first">
                <span className="title-word">Elevate</span>
                <span className="title-word">Your</span>
              </span>
              <span className="title-line title-main">
                <span className="gradient-text">Digital</span>
                <span className="gradient-text">Presence</span>
              </span>
            </h1>
            <div className="hero-subtitle-wrapper">
              <p className="hero-subtitle">
                <span className="hero-subtitle-line hero-subtitle-line--primary">
                  Auradrobe is your <span className="hero-subtitle-emphasis">digital innovation partner</span>, designing and building high-impact websites, mobile applications, brands, and growth strategies.
                </span>
                <span className="hero-subtitle-line hero-subtitle-line--secondary">
                  Built to drive measurable results.
                </span>
              </p>
            </div>
            <div className="hero-features">
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Data-Driven Strategy</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Design-Led Execution</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Scalable Digital Solutions</span>
              </div>
            </div>
            <div className="hero-cta-wrapper">
              <a href="#marketing" className="cta-button">
                <span>Explore Auradrobe →</span>
              </a>
              <div className="hero-stats">
                <div className="stat-item stat-item--partnership">
                  <span className="stat-number">Built With You</span>
                    <span className="stat-label">Long-Term Digital Partnerships</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">100+</span>
                  <span className="stat-label">Multi-Industry Partnerships</span>
                  <span className="stat-sublabel">Delivering Scalable Solutions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Section */}
      <section id="marketing" className="services marketing-section">
        <div className="container">
          <h2 className="section-title" data-reveal="center" data-reveal-scrub data-scroll-anchor="true">What we do.</h2>

          <div className="marketing-panel marketing-panel--capabilities marketing-capabilities-panel full-bleed" data-reveal="flow-right" data-reveal-scrub>
            <div className="capabilities-header">
              <div
                key={`cap-header-${capabilityIndex}`}
                className={`capabilities-header-title capabilities-header-title--${activeCapability?.titleAnim || 'glide-left'}`}
              >
                {activeCapability?.imageTitle}
              </div>
            </div>

            <div className="capabilities-carousel" role="region" aria-label="Marketing capabilities carousel">
              <div
                className="capabilities-stage"
                aria-live="polite"
                tabIndex={0}
                onKeyDown={handleCapabilitiesKeyDown}
              >
                <button
                  type="button"
                  className="capabilities-arrow capabilities-arrow--left"
                  onClick={() => goCapability('prev')}
                  aria-label="Previous capability"
                >
                  ←
                </button>

                <div
                  key={`${capabilityIndex}-${activeCapability?.imageTitle}`}
                  className={`capabilities-slide capabilities-slide--${capabilityDir}`}
                >
                  <div className="capabilities-media" aria-hidden="true">
                    {activeCapability?.image ? (
                      <img 
                        src={activeCapability.image} 
                        alt={activeCapability.imageTitle}
                        className="capabilities-image"
                      />
                    ) : (
                      <div className="capabilities-media-placeholder">Image placeholder</div>
                    )}
                  </div>
                  <div className="capabilities-copy">
                    <p className="capabilities-kicker">
                      {String(capabilityIndex + 1).padStart(2, '0')} / {String(marketingCapabilities.length).padStart(2, '0')}
                    </p>
                    <h4 className="capabilities-subtitle">{activeCapability?.subtitle}</h4>
                    <p className="capabilities-body">{activeCapability?.body}</p>
                  </div>
                </div>

                <div className="capabilities-dots" role="tablist" aria-label="Select capability">
                  {marketingCapabilities.map((cap, idx) => (
                    <button
                      key={cap.imageTitle}
                      type="button"
                      className={`capabilities-dot ${idx === capabilityIndex ? 'is-active' : ''}`}
                      onClick={() => {
                        setCapabilityDir(idx < capabilityIndex ? 'prev' : 'next');
                        setCapabilityIndex(idx);
                      }}
                      aria-label={`Show ${cap.imageTitle}`}
                      aria-pressed={idx === capabilityIndex}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  className="capabilities-arrow capabilities-arrow--right"
                  onClick={() => goCapability('next')}
                  aria-label="Next capability"
                >
                  →
                </button>
              </div>
            </div>
          </div>

          <div className="marketing-closer" data-reveal="flow-up" data-reveal-scrub>
            <p>If you're looking for a marketing vendor, we're not the right fit.</p>
            <p>If you're looking for a strategic partner — <strong className="gold-highlight">we are.</strong></p>
          </div>
        </div>
      </section>

      {/* Design Section */}
      <section 
        id="design" 
        className="services design-section"
        style={{ backgroundImage: 'url(/nextbg.png)' }}
      >
        <div className="container">
          <h2 className="section-title section-title--stacked" data-reveal="center" aria-label="Design That Builds Authority">
            {['Design', 'That', 'Builds', 'Authority'].map((word, w) => (
              <span
                key={word}
                className={`stack-word ${word === 'Authority' ? 'stack-word--authority' : ''}`}
                style={{ '--w': w }}
              >
                {Array.from(word).map((ch, i) => (
                  <span key={`${word}-${i}`} className="stack-letter" style={{ '--i': i }}>
                    {ch}
                  </span>
                ))}
              </span>
            ))}
          </h2>

          <div className="design-capabilities">
            <h3 className="design-subtitle" data-reveal="center">Crafting the Foundations of Trust</h3>
            <div className="design-cards">
              <div className="design-card" data-reveal="flow-up" style={{ '--stagger': 0 }}>
                <h4>Branding</h4>
                <p>Cohesive brand systems built to establish credibility and recognition.</p>
                <ul className="design-bullets">
                  <li>Visual direction</li>
                  <li>Logo systems</li>
                  <li>Typography &amp; color systems</li>
                  <li>Brand guidelines</li>
                </ul>
              </div>
              <div className="design-card" data-reveal="flow-up" style={{ '--stagger': 1 }}>
                <h4>Design Systems</h4>
                <p>Scalable design foundations that keep products consistent, efficient, and ready to grow.</p>
                <ul className="design-bullets">
                  <li>Component libraries</li>
                  <li>Color, typography &amp; spacing tokens</li>
                  <li>Responsive layout rules</li>
                  <li>Built for long-term scalability</li>
                </ul>
              </div>
              <div className="design-card" data-reveal="flow-up" style={{ '--stagger': 2 }}>
                <h4>Website</h4>
                <p>High-impact websites designed for clarity, conversion, and growth.</p>
                <ul className="design-bullets">
                  <li>Strategy-led UX</li>
                  <li>Premium UI systems</li>
                  <li>Responsive, scalable layouts</li>
                  <li>Performance-optimized builds</li>
                </ul>
              </div>
              <div className="design-card" data-reveal="flow-up" style={{ '--stagger': 3 }}>
                <h4>App</h4>
                <p>Product-focused UI/UX for mobile and web applications.</p>
                <ul className="design-bullets">
                  <li>User flows &amp; interface design</li>
                  <li>Design systems for scale</li>
                  <li>Developer-ready handoff</li>
                  <li>Platform-native experiences</li>
                </ul>
              </div>
            </div>

            <div className="design-promise" data-reveal="center">
              <p className="design-promise-kicker">Our Design Promise</p>
              <h4 className="design-promise-title">100% Satisfaction Guaranteed</h4>
              <p className="design-promise-body">
                Partner-led collaboration and iterative refinement — we don’t consider design complete until you’re confident in it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <h2 className="section-title" data-reveal="center">Auradrobe by the Numbers</h2>
          
          {/* Stats Banner */}
          <div className="about-stats" data-reveal="center">
            <div className="stat-box">
              <span className="stat-number-large">15+ Years</span>
              <span className="stat-label-large">Combined Business & Digital Experience</span>
            </div>
            <div className="stat-box">
              <span className="stat-number-large">100+ Campaigns</span>
              <span className="stat-label-large">Delivered Across Digital Channels</span>
            </div>
            <div className="stat-box">
              <span className="stat-number-large">North America</span>
              <span className="stat-label-large">Nationwide Digital Reach</span>
            </div>
            <div className="stat-box">
              <span className="stat-number-large">Multi-Industry</span>
              <span className="stat-label-large">B2B &amp; B2C Market Experience</span>
            </div>
          </div>

        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <h2 className="section-title" data-reveal="center">Let's Work Together</h2>
          <p className="contact-intro" data-reveal="center">Let’s build the digital foundation that supports where you’re headed.</p>
          <div className="contact-cta" data-reveal="center">
            <button
              type="button"
              className="cta-button"
              onClick={() => navigate('/schedule')}
            >
              <span>Schedule a time to evolve your brand</span>
              <span className="cta-arrow">→</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-reveal="center">
        <div className="container">
          <p>2026 Auradrobe Corp. All rights reserved.</p>
        </div>
      </footer>
            </>
          )}
        />
        <Route path="/schedule" element={<SchedulePage />} />
      </Routes>
    </div>
  );
}

export default App;
